import React, { useState, useRef, useEffect, useCallback } from "react";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import StripePaymentElement from "./StripePaymentElement";
import OrderSummary from "./OrderSummary";
import PaymentSuccess from "./PaymentSuccess";
import ShippingBillingForm from "./ShippingBillingForm";
import ContactForm from "./ContactForm";
import ShippingInfoModal from "./ShippingInfoModal";
import ValidatedAddressModal from "./ValidatedAddressModal";
import { isService, updateSoldDate } from "../utils/cartUtils";
import { createShippoLabel } from "../utils/shippo";

const CheckoutModal = ({
  checkoutStep,
  checkoutTitles,
  cartItems = [],
  customerInfo = {},
  showBilling,
  setShow,
  handleCustomerInfoChange,
  handleBillingCheckboxChange,
  isValidEmail,
  isValidUSPhone,
  isValidAddress,
  isFormValid,
  isBillingValid,
  handlePrevStep,
  handleNextStep,
  handleSubmit: handleSubmitProp,
  stripeReady,
  stripeComplete,
  clientSecret,
  showCheckoutModal,
  handleStripeReady,
  handleStripeChange,
  checkoutError,
  handlePaymentSuccessAndInventory,
  showPaymentSuccess,
  totalCartPrice,
  handleRemoveFromCart,
  setCheckoutError,
  storeCustomerInfo,
  updateInventory,
  setShowPaymentSuccess,
  setCheckoutStep,
  handleProductClick,
  handleUpdateCartQuantity,
  handleUpdatePairsToShip,
  taxAmount,
  finalShippingCost,
  finalProcessingFee,
  finalTotalWithFee,
  finalTaxAmount,
  paymentIntentReady,
  services,
  setClientSecret,
  setFinalShippingCost,
  setFinalTaxAmount,
  setFinalProcessingFee,
  setFinalTotalWithFee,
  setPaymentIntentReady,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  // small UI helpers / refs used throughout the component
  const [loading, setLoading] = useState(false);
  const [showCountryTooltip, setShowCountryTooltip] = useState(false);
  const [continueDisabled, setContinueDisabled] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showShippingInfo, setShowShippingInfo] = useState(false);
  const [showValidatedAddressModal, setShowValidatedAddressModal] = useState(false);
  const [addressChoice, setAddressChoice] = useState("validated");

  const hasProducts =
    Array.isArray(cartItems) && cartItems.some((i) => !isService(i));
  const hasServices =
    Array.isArray(cartItems) && cartItems.some((i) => isService(i));

  // product shipping returned from server (in cents)
  const [, setProductShippingCents] = useState(null);
  const [, setFetchingShipping] = useState(false);
  // keep a ref with the latest fetched cents to avoid stale-state races
  const productShippingCentsRef = useRef(null);

  // Store validated address in a ref so it survives payment flow
  const [validatedAddress, setValidatedAddress] = useState(null);
  
  // Store which address was selected by user in modal (validated or given)
  const [selectedAddressType, setSelectedAddressType] = useState(null);

  // Persist across renders/HMR
  if (!window.__kwc_validatedAddress) window.__kwc_validatedAddress = null;
  if (!window.__kwc_selectedAddressType) window.__kwc_selectedAddressType = null;
  const validatedAddressRef = useRef(window.__kwc_validatedAddress);
  const selectedAddressTypeRef = useRef(window.__kwc_selectedAddressType);

  // When you set validatedAddress on step 3, also persist to window and ref
  useEffect(() => {
    if (validatedAddress) {
      validatedAddressRef.current = validatedAddress;
      window.__kwc_validatedAddress = validatedAddress;
    }
  }, [validatedAddress]);

  // When you set selectedAddressType, persist to window and ref
  useEffect(() => {
    if (selectedAddressType) {
      selectedAddressTypeRef.current = selectedAddressType;
      window.__kwc_selectedAddressType = selectedAddressType;
    }
  }, [selectedAddressType]);

  // Do NOT clear when moving forward. Only clear when user goes back to step 3 explicitly.
  const prevStep = useRef(checkoutStep);
  useEffect(() => {
    const wentBackToStep3 = prevStep.current > checkoutStep && checkoutStep === 3;
    if (wentBackToStep3) {
      setValidatedAddress(null);
      validatedAddressRef.current = null;
      window.__kwc_validatedAddress = null;
      setPaymentIntentReady(false);
    }
    prevStep.current = checkoutStep;
  }, [checkoutStep, setPaymentIntentReady]);

  // fetch product shipping from Netlify calculate-shipping (returns amount in dollars)
  const fetchProductShipping = useCallback(async () => {
    
    const addr = {
      name:
        `${customerInfo.firstName || ""} ${
          customerInfo.lastName || ""
        }`.trim() || "Customer",
      street1: customerInfo.shippingStreet,
      street2: customerInfo.shippingLine2 || "",
      city: customerInfo.shippingCity,
      state: customerInfo.shippingState,
      zip: customerInfo.shippingPostal,
      country: customerInfo.shippingCountry || "US",
    };
    if (!addr.street1 || !addr.city || !addr.state || !addr.zip) {
      throw new Error("Incomplete shipping address");
    }

    setFetchingShipping(true);
    try {
      const res = await fetch("/.netlify/functions/calculate-shipping", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cartItems,
          shippingAddress: addr,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        setAddressSuggestions(json?.suggestionMessages || []);
        setCheckoutError(json?.error || "Failed to fetch shipping rates");
        setFetchingShipping(false);
        return null;
      }
      setAddressSuggestions([]); // Clear suggestions on success
      setCheckoutError(""); // Clear error on success

      // Store the validated address from Shippo
      if (json?.validatedAddress) {
        setValidatedAddress(json.validatedAddress);
        validatedAddressRef.current = json.validatedAddress;
        
      }

      // Prefer explicit Cents returned by the server (amount_cents) to avoid ambiguity
      const explicitCents = Number(
        json?.amount_cents ??
          json?.amountCents ??
          json?.shippingCostCents ??
          json?.shipping_cost_cents ??
          NaN
      );
      let cents;
      if (Number.isFinite(explicitCents) && explicitCents > 0) {
        cents = Math.round(explicitCents);
      } else {
        // Fallback: server returned dollars -> convert to cents
        const amountDollars = Number(json?.amount ?? json?.shippingCost ?? 0);
        cents = Math.round((amountDollars || 0) * 100);
        
      }

      setProductShippingCents(cents);
      productShippingCentsRef.current = cents;
      return cents;
    } catch (err) {
      // If the error is a fetch Response, parse the JSON to get suggestionMessages
      if (err instanceof Response) {
        try {
          const json = await err.json();
          const suggestions = json?.suggestionMessages || [];
          setAddressSuggestions(suggestions);
          const error = new Error(
            json?.error || "Failed to fetch shipping rates"
          );
          error.suggestionMessages = suggestions;
          throw error;
        } catch (e) {
          setAddressSuggestions([]);
          throw err;
        }
      } else if (err && err.suggestionMessages) {
        setAddressSuggestions(err.suggestionMessages);
      } else if (
        err &&
        err.message &&
        err.message.includes("Invalid shipping address")
      ) {
        // Try to extract suggestionMessages from the error object itself (Netlify sometimes attaches them)
        setAddressSuggestions(err.suggestionMessages || []);
      } else {
        setAddressSuggestions([]);
      }
      throw err;
    } finally {
      setFetchingShipping(false);
    }
  }, [
    cartItems,
    customerInfo,
    setCheckoutError, // <-- Add this
    setValidatedAddress,
    setAddressSuggestions,
    setFetchingShipping,         // <-- add this
    setProductShippingCents,     // <-- add this
  ]);

  // expose fetchProductShipping helper for manual testing from the console
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.__debugFetchProductShipping = fetchProductShipping;
      return () => {
        try {
          delete window.__debugFetchProductShipping;
        } catch (e) {}
      };
    }
    return undefined;
  }, [fetchProductShipping]);

  // remove auto-fetch on step 4; instead fetch product shipping when user clicks Continue on step 3
  // This callback will run when the Continue button is pressed on step 3, ensuring the shipping
  // amount is resolved and stored before the UI advances to the payment step.
  const handleNextWrapped = useCallback(
    async (ev) => {
      if (
        checkoutStep === 3 &&
        (hasProducts || hasServices) &&
        isFormValid()
      ) {
        setContinueDisabled(true);
        let validShipping = true;

        try {
          const returned = await fetchProductShipping();
  
          if (
            typeof returned !== "number" ||
            !Number.isFinite(returned) ||
            returned < 0
          ) {
            setCheckoutError(
              "Invalid shipping address. Please check your shipping address fields and click Continue."
            );
            validShipping = false;
            setContinueDisabled(false);
          } else {
            setCheckoutError("");
            setProductShippingCents(Math.round(returned));
            productShippingCentsRef.current = Math.round(returned);
          }
        } catch (err) {
          let suggestions = [];
          if (err && err.suggestionMessages) {
            suggestions = err.suggestionMessages;
          } else if (addressSuggestions.length > 0) {
            suggestions = addressSuggestions;
          }
          setAddressSuggestions(suggestions);
          setCheckoutError(
            err?.message ||
              "Invalid shipping address. Please check your shipping address fields and click Continue."
          );
          validShipping = false;
          setContinueDisabled(false);
        }

        // Show modal if validated address is present (shipping validation is always needed)
        if (validShipping && validatedAddressRef.current) {
          setShowValidatedAddressModal(true);
          setAddressChoice("validated");
          setContinueDisabled(false);
          return;
        }

        // Otherwise, proceed as before
        if (validShipping) {
          const billingAddress = !showBilling
            ? {
                line1: customerInfo.shippingStreet,
                line2: customerInfo.shippingLine2 || "",
                city: customerInfo.shippingCity,
                state: customerInfo.shippingState,
                postal_code: customerInfo.shippingPostal,
                country: customerInfo.shippingCountry,
              }
            : {
                line1: customerInfo.billingStreet,
                line2: customerInfo.billingLine2 || "",
                city: customerInfo.billingCity,
                state: customerInfo.billingState,
                postal_code: customerInfo.billingPostal,
                country: customerInfo.billingCountry,
              };

          try {
            const res = await fetch(
              "/.netlify/functions/create-payment-intent",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  cartItems,
                  shippingCostCents: Math.round(productShippingCentsRef.current),
                  billingAddress,
                }),
              }
            );
            const data = await res.json();
            const clientSecret = data.clientSecret || data.client_secret;

            if (!res.ok) {
              setCheckoutError(data.error || "Checkout failed. Try again.");
              setContinueDisabled(false);
              return;
            }

            setCheckoutError("");
            setClientSecret(clientSecret);
            setFinalShippingCost(data.shippingCostCents ?? data.shippingCost ?? 0);
            setFinalTaxAmount(data.taxAmount ?? 0);
            setFinalProcessingFee(data.processingFee ?? 0);
            setFinalTotalWithFee(data.totalWithFee ?? 0);
            setPaymentIntentReady(true);
            setCheckoutStep(4);
          } catch (err) {
            console.log("catch create-payment-intent response error:", err);
            setCheckoutError(err?.message || "Checkout failed. Try again.");
            setContinueDisabled(false);
          }
        }
      } else {
        handleNextStep();
      }
    },
    [
      cartItems,
      customerInfo.billingCity,
      customerInfo.billingCountry,
      customerInfo.billingPostal,
      customerInfo.billingState,
      customerInfo.billingStreet,
      customerInfo.billingLine2,
      customerInfo.shippingCity,
      customerInfo.shippingCountry,
      customerInfo.shippingPostal,
      customerInfo.shippingState,
      customerInfo.shippingStreet,
      customerInfo.shippingLine2,
      fetchProductShipping,
      handleNextStep,
      hasProducts,
      hasServices,
      setCheckoutError,
      setCheckoutStep,
      setClientSecret,
      setFinalProcessingFee,
      setFinalShippingCost,
      setFinalTaxAmount,
      setFinalTotalWithFee,
      setPaymentIntentReady,
      showBilling,
      checkoutStep,
      isFormValid,
      addressSuggestions,
      setProductShippingCents,
      setShowValidatedAddressModal,
      setAddressChoice,
      validatedAddressRef,
    ]
  );

  // Submit order
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setCheckoutError("Stripe is not loaded.");
      return;
    }
    if (!stripeComplete) {
      setCheckoutError("Please enter your payment details.");
      return;
    }
    setCheckoutError("");
    setLoading(true);

    // confirm payment with Stripe
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.origin + "/success" },
      redirect: "if_required",
    });

    if (error) {
      setCheckoutError(error.message || "Payment failed.");
      setLoading(false);
      return;
    }

    // Build order_summary (values in cents) — use the server-calculated shipping we sent
    const order_summary = {
      subtotal: totalCartPrice,
      shippingCost: finalShippingCost,
      processingFee: finalProcessingFee,
      taxAmount: finalTaxAmount,
      totalWithFee: finalTotalWithFee,
      currency: "USD",
    };

    // Ensure billing fields are populated when "billing same as shipping" is selected
    const customerPayload = { ...customerInfo };
    if (!showBilling) {
      customerPayload.billingStreet = customerPayload.shippingStreet;
      customerPayload.billingLine2 = customerPayload.shippingLine2 || "";
      customerPayload.billingCity = customerPayload.shippingCity;
      customerPayload.billingState = customerPayload.shippingState;
      customerPayload.billingPostal = customerPayload.shippingPostal;
      customerPayload.billingCountry = customerPayload.shippingCountry;
    }

    // Persist order
    let savedOrder = null;
    try {
      const payload = {
        customer: customerPayload,
        order_summary,
        products: cartItems,
      };

      const res = await fetch("/.netlify/functions/store-customer-info", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        console.error("store-customer-info failed:", res.status, txt);
        setCheckoutError("Failed to save order. Please try again.");
        setLoading(false);
        return;
      }

      const body = await res.json().catch(() => null);
      savedOrder = body?.order || body || null;
    } catch (err) {
      console.error("Failed to call store-customer-info directly:", err);
      setCheckoutError("Failed to save order. Please try again.");
      setLoading(false);
      return;
    }

    const orderId =
      savedOrder?.orderId ||
      savedOrder?.id ||
      savedOrder?.key ||
      savedOrder?._id ||
      null;

    if (!orderId) {
      console.error("store-customer-info did not return orderId", savedOrder);
      setCheckoutError(
        "Order was saved but confirmation number is missing. Please contact support."
      );
      setLoading(false);
      return;
    }

    // Ship each physical item using the validated address if present
    for (const item of cartItems) {
      if (!isService(item, services)) {
        await updateInventory(item.id, item.quantity || 1);
        await updateSoldDate(item.id);
        for (let i = 0; i < (item.quantity || 1); i++) {
          // Use the selected address type (validated or given)
          let addressToUse;
          
          const addressTypeToUse = selectedAddressType || selectedAddressTypeRef.current;
    
          if (addressTypeToUse === "validated" && validatedAddressRef.current) {
            // User selected the validated address
            const v = validatedAddressRef.current;
            addressToUse = {
              firstName: v.name?.split(" ")[0] || customerInfo.firstName,
              lastName: v.name?.split(" ").slice(1).join(" ") || customerInfo.lastName,
              shippingStreet: v.street1,
              shippingLine2: v.street2 || "",
              shippingCity: v.city,
              shippingState: v.state,
              shippingPostal: v.zip,
              shippingCountry: v.country || "US",
              objectId: v.objectId, // signals Shippo-validated address
            };
          } else {
            // User selected the given address or no modal was shown
            addressToUse = customerInfo;
          }

          await createShippoLabel(orderId, addressToUse, [item]);
        }
      }
    }

    // Send confirmation emails
    try {
      const hasService =
        Array.isArray(cartItems) &&
        cartItems.some((item) => isService(item, services));

      await fetch("/.netlify/functions/send-confirmation-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer: customerPayload,
          products: cartItems,
          shippingCost: finalShippingCost,
          totalWithFee: finalTotalWithFee,
          orderId,
          order_summary,
          hasService,
        }),
      });

      if (hasService) {
        await fetch("/.netlify/functions/send-reice-confirmation-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            customer: customerPayload,
            products: cartItems,
            orderId,
            order_summary,
          }),
        });
      }
    } catch (err) {
      console.error("Failed to send confirmation email:", err);
    }

    setLoading(false);
    setShowPaymentSuccess(true);
    setCheckoutStep(4);
  };

  // Clear validated address and payment intent on checkout step change
  useEffect(() => {
    if (prevStep.current > checkoutStep && checkoutStep === 3) {
      setValidatedAddress(null);
      setPaymentIntentReady(false);
      validatedAddressRef.current = null; // <-- Also clear the ref
    }
    prevStep.current = checkoutStep;
  }, [checkoutStep, setPaymentIntentReady]);

  // NEW: if we are showing the success screen, render it exclusively (hide the rest)
  if (showPaymentSuccess) {
    return (
      <div className="flex flex-col h-full">
        <PaymentSuccess onOk={handlePaymentSuccessAndInventory} />
      </div>
    );
  }

  const givenAddress = {
    name: `${customerInfo.firstName || ""} ${customerInfo.lastName || ""}`.trim(),
    street1: customerInfo.shippingStreet,
    street2: customerInfo.shippingLine2 || "",
    city: customerInfo.shippingCity,
    state: customerInfo.shippingState,
    zip: customerInfo.shippingPostal,
    country: customerInfo.shippingCountry || "US",
  };

  const handleValidatedAddressContinue = async (choice) => {
    setAddressChoice(choice);
    setSelectedAddressType(choice); // Store which address was selected
    setShowValidatedAddressModal(false);
    setContinueDisabled(true);

    let chosen;
    if (choice === "validated" && validatedAddressRef.current) {
      const v = validatedAddressRef.current;
      chosen = {
        line1: v.street1,
        line2: v.street2 || "",
        city: v.city,
        state: v.state,
        postal_code: v.zip,
        country: v.country || "US",
      };
    } else {
      chosen = {
        line1: customerInfo.shippingStreet,
        line2: customerInfo.shippingLine2 || "",
        city: customerInfo.shippingCity,
        state: customerInfo.shippingState,
        postal_code: customerInfo.shippingPostal,
        country: customerInfo.shippingCountry,
      };
    }

    try {
      const res = await fetch("/.netlify/functions/create-payment-intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cartItems,
          shippingCostCents: Math.round(productShippingCentsRef.current),
          billingAddress: chosen,
        }),
      });
      const data = await res.json();
      const clientSecret = data.clientSecret || data.client_secret;

      if (!res.ok) {
        setCheckoutError(data.error || "Checkout failed. Try again.");
        setContinueDisabled(false);
        return;
      }

      setCheckoutError("");
      setClientSecret(clientSecret);
      setFinalShippingCost(data.shippingCostCents ?? data.shippingCost ?? 0);
      setFinalTaxAmount(data.taxAmount ?? 0);
      setFinalProcessingFee(data.processingFee ?? 0);
      setFinalTotalWithFee(data.totalWithFee ?? 0);
      setPaymentIntentReady(true);
      setCheckoutStep(4);
    } catch (err) {
      setCheckoutError(err?.message || "Checkout failed. Try again.");
    } finally {
      setContinueDisabled(false);
    }
  };

  // Defensive fallback for AddressBlock props
  const safeAddress = (address) => {
    // If address is not an object, or is null, return all keys as empty strings
    if (!address || typeof address !== 'object') {
      return {
        name: '',
        street1: '',
        street2: '',
        city: '',
        state: '',
        zip: '',
        country: '',
      };
    }
    // If address is an object, but missing keys, fill them in
    // If address is an array, treat as invalid
    if (Array.isArray(address)) {
      return {
        name: '',
        street1: '',
        street2: '',
        city: '',
        state: '',
        zip: '',
        country: '',
      };
    }
    return {
      name: typeof address.name === 'string' ? address.name : '',
      street1: typeof address.street1 === 'string' ? address.street1 : '',
      street2: typeof address.street2 === 'string' ? address.street2 : '',
      city: typeof address.city === 'string' ? address.city : '',
      state: typeof address.state === 'string' ? address.state : '',
      zip: typeof address.zip === 'string' ? address.zip : '',
      country: typeof address.country === 'string' ? address.country : '',
    };
  };

  return (
    <>
      <div className="flex flex-col h-full">
        <div className="text-center py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {checkoutTitles[checkoutStep - 1]}
          </h2>
          <p className="text-gray-500 mt-1">Step {checkoutStep} of 4</p>
        </div>
        <div className="flex-1 overflow-y-auto py-6 px-4">
          {/* Step 1: Cart */}
          {checkoutStep === 1 && (
            <OrderSummary
              cartItems={cartItems}
              totalCartPrice={totalCartPrice}
              checkoutStep={checkoutStep}
              handleProductClick={handleProductClick}
              handleUpdateCartQuantity={handleUpdateCartQuantity}
              handleUpdatePairsToShip={handleUpdatePairsToShip}
              handleRemoveFromCart={handleRemoveFromCart}
              showShippingInfo={showShippingInfo}
              setShowShippingInfo={setShowShippingInfo}
            />
          )}

          {/* Step 2: Contact Information */}
          {checkoutStep === 2 && (
            <ContactForm
              customerInfo={customerInfo}
              handleCustomerInfoChange={handleCustomerInfoChange}
              isValidEmail={isValidEmail}
              isValidUSPhone={isValidUSPhone}
            />
          )}

          {/* Step 3: Shipping & Billing */}
          {checkoutStep === 3 && (
            <>
              <ShippingBillingForm
                customerInfo={customerInfo}
                handleCustomerInfoChange={handleCustomerInfoChange}
                handleBillingCheckboxChange={handleBillingCheckboxChange}
                isValidAddress={isValidAddress}
                showBilling={showBilling}
                showCountryTooltip={showCountryTooltip}
                setShowCountryTooltip={setShowCountryTooltip}
              />
              {(() => {
                // <--- Add this IIFE to log before rendering
                return null;
              })()}
              {checkoutError && (
                <div className="text-red-600 text-base font-semibold mt-2 text-center">
                  {addressSuggestions.length > 0 ? (
                    <ul className="mt-2 text-base text-red-600 text-left inline-block list-none">
                      {addressSuggestions.map((msg, idx) => (
                        <li key={idx} className="mb-2">
                          <span className="text-base text-red-600 block font-semibold">
                            {msg.code}:
                          </span>
                          <span className="text-base text-red-600">
                            {msg.text || msg.message || JSON.stringify(msg)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    checkoutError
                  )}
                </div>
              )}
            </>
          )}

          {/* Step 4: Payment */}
          {checkoutStep === 4 && (
            <form id="checkout-form" onSubmit={handleSubmit}>
              {!paymentIntentReady ? (
                <div className="text-center py-8 text-lg text-gray-500">
                  Calculating your order...
                </div>
              ) : (
                <OrderSummary
                  cartItems={cartItems}
                  shippingCost={finalShippingCost}
                  processingFee={finalProcessingFee}
                  totalWithFee={finalTotalWithFee}
                  totalCartPrice={totalCartPrice}
                  taxAmount={finalTaxAmount}
                  paymentIntentReady={paymentIntentReady}
                  checkoutStep={checkoutStep}
                  handleProductClick={handleProductClick}
                  handleUpdateCartQuantity={handleUpdateCartQuantity}
                  showShippingInfo={showShippingInfo}
                  setShowShippingInfo={setShowShippingInfo}
                />
              )}
              <div className="mt-6">
                <StripePaymentElement
                  clientSecret={clientSecret}
                  showCheckoutModal={showCheckoutModal}
                  onReady={handleStripeReady}
                  onChange={handleStripeChange}
                />
              </div>
              <div className="relative mt-6">
                <button
                  type="submit"
                  disabled={loading || !stripeReady || !stripeComplete}
                  className={`w-full py-4 px-8 bg-indigo-600 text-white rounded-full shadow-lg font-bold text-xl transition-colors ${
                    loading || !stripeReady || !stripeComplete
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-indigo-700"
                  }`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin h-6 w-6 mr-2 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8z"
                        ></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    "Pay Now"
                  )}
                </button>
                {checkoutError && (
                  <div className="text-red-600 text-base font-semibold mt-2 text-center">
                    {checkoutError}
                    {addressSuggestions.length > 0 && (
                      <ul className="mt-2 text-sm text-yellow-700 text-left inline-block">
                        {addressSuggestions.map((msg, idx) => (
                          <li key={idx} className="mb-1">
            <span className="font-mono text-xs text-gray-500">{msg.code}</span>
                            <br />
            <span>{msg.text || msg.message || JSON.stringify(msg)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </form>
          )}

          {/* Payment Success Screen */}
          {showPaymentSuccess && (
            <PaymentSuccess onOk={handlePaymentSuccessAndInventory} />
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="px-4 py-4 border-t border-gray-200">
          {(checkoutStep === 1 || checkoutStep === 4) && (
            <div className="flex items-center w-full">
              {/* Back button on step 4, left-aligned */}
              {checkoutStep === 4 && (
                <button
                  className="py-2 px-6 rounded-full font-bold bg-gray-200 text-gray-700 hover:bg-gray-300 transition mr-4"
                  onClick={handlePrevStep}
                >
                  Back
                </button>
              )}
              {/* Payment methods message: left on step 1, right on step 4 */}
              <div className={`flex-1 ${checkoutStep === 4 ? "text-right" : ""}`}>
                <div className="text-lg text-gray-700 font-semibold sm:text-lg text-base">
                  We accept Visa Credit Card, Amazon Pay, Apple Pay, Cash App
                  Pay, and Google Pay.
                </div>
              </div>
              {/* Continue button, right-aligned (only on step 1) */}
              {checkoutStep === 1 && (
                <button
                  className={`py-2 px-6 rounded-full font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition
            ${cartItems.length === 0 ? "opacity-50 cursor-not-allowed" : ""}
            sm:ml-0 ml-8
          `}
                  onClick={handleNextStep}
                  disabled={cartItems.length === 0}
                >
                  Continue
                </button>
              )}
            </div>
          )}
          {/* Back button always left, Continue always right on steps 2 and 3 */}
          {(checkoutStep === 2 || checkoutStep === 3) && (
            <div className="flex justify-between items-center w-full mt-2">
              <div>
                <button
                  className="py-2 px-6 rounded-full font-bold bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
                  onClick={handlePrevStep}
                >
                  Back
                </button>
              </div>
              <div>
                <button
                  className={`py-2 px-6 rounded-full font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition ${
                    continueDisabled ||
                    cartItems.length === 0 ||
                    (checkoutStep === 2 &&
                      (!customerInfo.firstName?.trim() ||
                        !customerInfo.lastName?.trim() ||
                        !customerInfo.email?.trim() ||
                        customerInfo.firstName.length > 40 ||
                        customerInfo.lastName.length > 40 ||
                        customerInfo.email.length > 60 ||
                        !isValidEmail(customerInfo.email) ||
                        !customerInfo.phone?.trim() ||
                        !isValidUSPhone(customerInfo.phone))) ||
                    (checkoutStep === 3 &&
                      (!isFormValid() ||
                        !isBillingValid() ||
                        !customerInfo.shippingPostal ||
                        !/^\d{4,}$/.test(customerInfo.shippingPostal) ||
                        (showBilling &&
                          (!customerInfo.billingPostal ||
                            !/^\d{4,}$/.test(customerInfo.billingPostal)))))
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                  onClick={handleNextWrapped}
                  disabled={
                    continueDisabled ||
                    cartItems.length === 0 ||
                    (checkoutStep === 2 &&
                      (!customerInfo.firstName?.trim() ||
                        !customerInfo.lastName?.trim() ||
                        !customerInfo.email?.trim() ||
                        customerInfo.firstName.length > 40 ||
                        customerInfo.lastName.length > 40 ||
                        customerInfo.email.length > 60 ||
                        !isValidEmail(customerInfo.email) ||
                        !customerInfo.phone?.trim() ||
                        !isValidUSPhone(customerInfo.phone))) ||
                    (checkoutStep === 3 &&
                      (!isFormValid() ||
                        !isBillingValid() ||
                        !customerInfo.shippingPostal ||
                        !/^\d{4,}$/.test(customerInfo.shippingPostal) ||
                        (showBilling &&
                          (!customerInfo.billingPostal ||
                            !/^\d{4,}$/.test(customerInfo.billingPostal)))))
                  }
                >
                  {continueDisabled && checkoutStep === 3
                    ? "Calculating Shipping"
                    : "Continue"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Shipping Info Modal OUTSIDE main checkout div */}
      <ShippingInfoModal
        show={showShippingInfo}
        onClose={() => setShowShippingInfo(false)}
      />

      {/* Validated Address Modal */}
      {showValidatedAddressModal && validatedAddressRef.current && (
        <ValidatedAddressModal
          show={showValidatedAddressModal}
          recommendedAddress={safeAddress(validatedAddressRef.current)}
          givenAddress={safeAddress(givenAddress)}
          selected={addressChoice}
          onSelect={setAddressChoice}
          onContinue={handleValidatedAddressContinue}
          onClose={() => setShowValidatedAddressModal(false)}
        />
      )}
    </>
  );
}

export default CheckoutModal;
